import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthBackground from '../components/auth/AuthBackground';
import AuthNavbar from '../components/navigation/auth';

import { useAuth } from '../contexts/AuthContext';
import { STORAGE_KEYS } from '../constants/storage';

/**
 * Login Page
 * 
 * User login page with email/password authentication and social login options.
 * 
 * Exact styles from auth.css:
 * - Card max width: 420px
 * - Card padding: 32px 32px 28px
 * - Card border radius: 16px
 * - Background: rgba(255, 255, 255, 0.05) with backdrop blur 20px
 * - Shadow: 0 20px 45px rgba(0, 0, 0, 0.3), 0 0 30px rgba(255, 255, 255, 0.05)
 * - Border: 1px solid rgba(255, 255, 255, 0.1)
 */
const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn, resetPassword, error: authError, loading: authLoading } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => setIsVisible(true), 80);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Clear errors when form data changes
  useEffect(() => {
    setLocalError(null);
  }, [formData.email, formData.password]);

  // Display auth errors
  useEffect(() => {
    if (authError) {
      setLocalError(authError);
    }
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setIsSubmitting(true);

    try {
      await signIn(formData.email, formData.password);
      
      // Store rememberMe preference in localStorage (UI preference only)
      if (formData.rememberMe) {
        localStorage.setItem(STORAGE_KEYS.USER_EMAIL, formData.email);
      }
      
      // Redirect to dashboard after successful login
      navigate('/dashboard');
    } catch (error) {
      // Error is already handled by AuthContext and displayed via authError
      // Just ensure we don't navigate on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setLocalError('Please enter your email address first');
      return;
    }

    try {
      await resetPassword(formData.email);
      setLocalError(null);
      // Show success message (could be improved with a toast notification)
      alert('Password reset email sent! Please check your inbox.');
    } catch (error) {
      // Error is handled by AuthContext and displayed via authError
    }
  };

  return (
    <div 
      className="auth-page min-h-screen bg-main-gradient relative overflow-x-hidden w-full" 
      style={{ 
        height: '100vh', 
        overflowY: 'auto',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%'
      }}
    >
      {/* Glowing rectangle background */}
      <AuthBackground />

      {/* Auth navbar */}
      <AuthNavbar />

      {/* Main auth panel */}
      <div className="auth-main bg-transparent relative z-2 pt-16 min-h-[calc(100vh-64px)]">
        <div 
          className="auth-panel flex-1 flex items-center justify-center py-4 md:py-8 px-3 md:px-6 pb-6 md:pb-10 min-h-full"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0) scale(1)' : 'translateX(25px) scale(0.97)',
            transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: isVisible ? 'auto' : 'transform, opacity',
          }}
        >
          {/* Auth card - MOBILE: Full width */}
          <div className="auth-card w-full md:max-w-auth-card glass-bg-auth rounded-auth-card py-6 md:py-8 px-6 md:px-8 pb-5 md:pb-7 shadow-auth-card border border-white/10 relative z-2 transition-all duration-300 hover:shadow-auth-card-hover">
            {/* Title */}
            <h1 className="auth-title text-xl md:text-auth-title font-bold mb-1 text-white">
              Welcome Back
            </h1>

            {/* Subtitle */}
            <p className="auth-subtitle text-xs md:text-auth-subtitle text-[#a0a0a0] mb-4 md:mb-6">
              Log in to your <span className="lawnance-brand">LAWNANCE</span> account
            </p>

            {/* Error Message */}
            {localError && (
              <div className="mb-3 md:mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
                <p className="text-xs md:text-sm text-red-400">{localError}</p>
              </div>
            )}

            {/* Login Form */}
            <form className="auth-form flex flex-col gap-3 md:gap-[14px]" onSubmit={handleSubmit}>
              {/* Email Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.1s]">
                Email
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="auth-input"
                  required
                />
              </label>

              {/* Password Field */}
              <label className="auth-label flex flex-col gap-1.5 text-auth-label text-auth-label opacity-0 animate-fade-in-up-smooth [animation-delay:0.15s]">
                Password
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="auth-input"
                  required
                />
              </label>

              {/* Remember me & Forgot password */}
              <div className="auth-row flex items-center justify-between mt-1">
                <label className="auth-checkbox flex items-center gap-1.5 text-[12px] text-auth-label transition-colors duration-300 cursor-pointer hover:text-white">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="cursor-pointer transition-all duration-300"
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="auth-link-small text-white no-underline text-[12px] transition-opacity duration-200 hover:opacity-80 hover:underline bg-transparent border-none cursor-pointer p-0"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || authLoading}
                className="auth-primary-btn mt-1 rounded-full border-none px-4 py-2.5 text-auth-primary-btn font-medium bg-white/15 text-white cursor-pointer transition-all duration-300 border border-white/20 hover:bg-white/25 hover:-translate-y-0.5 hover:shadow-auth-primary-btn-hover hover:border-white/30 active:scale-[0.98] backdrop-blur-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isSubmitting || authLoading ? 'Logging in...' : 'Log In'}
              </button>
            </form>

            {/* Divider */}
            <div className="auth-divider flex items-center gap-2 text-[11px] text-[#6b7280] my-4 mt-4 mb-2">
              <div className="flex-1 h-px bg-white/15" />
              OR
              <div className="flex-1 h-px bg-white/15" />
            </div>

            {/* Social Login Buttons */}
            <div className="auth-social-row flex gap-2 md:gap-2 mb-1">
              <button
                type="button"
                className="auth-social-btn auth-social-google flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-full border border-white/15 bg-white/5 text-sm md:text-xs text-auth-label cursor-pointer transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 hover:shadow-auth-social-btn-hover hover:text-white active:scale-[0.98] min-h-[48px]"
              >
                <span className="auth-social-icon text-[13px] text-[#ea4335]">G</span>
                Google
              </button>
              <button
                type="button"
                className="auth-social-btn auth-social-microsoft flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-full border border-white/15 bg-white/5 text-sm md:text-xs text-auth-label cursor-pointer transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 hover:shadow-auth-social-btn-hover hover:text-white active:scale-[0.98] min-h-[48px]"
              >
                <span className="auth-social-icon text-[13px] text-[#2563eb]">M</span>
                Microsoft
              </button>
            </div>

            {/* Footer text */}
            <p className="auth-footer-text mt-2.5 text-[12px] text-[#9ca3af] text-center">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="auth-link text-white no-underline transition-opacity duration-200 hover:opacity-80 hover:underline"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;